/* =============================================================================
   COLLECTION GRID (NEW) — filter / sort / paginate
   -----------------------------------------------------------------------------
   This is the SAME mechanism the theme's existing collection-product-grid
   section already uses, re-pointed at this section's hooks. No new filter
   engine: Shopify's own facet params come straight off the form inputs
   (value.param_name / value.value), and the response is rendered by the
   Section Rendering API.

     form -> new FormData -> URLSearchParams -> pathname + '?' + params
          -> history.pushState
          -> fetch(url + '&section_id=<id>')
          -> swap [data-cgn-grid] / [data-cgn-pagination] / [data-cgn-toolbar]
             and the filter form's contents out of the parsed document
          -> hard-navigate to the same URL if the fetch fails

   Why it isn't literally the old file: that engine lives in an inline <script>
   inside collection-product-grid.liquid, hard-coded to that section's cpg__
   class names and to a custom button/listbox sort control. This section uses
   the reference's <select name="sort_by">.

   Listeners are delegated from `document` and scoped per section via
   closest('[data-cgn-section]'), so theme-editor section re-renders and
   multiple instances on one page both work with no re-init step.
   ============================================================================= */

(function () {
  'use strict';

  var DEBOUNCE_MS = 150;
  var timers = new WeakMap();
  var controllers = new WeakMap();

  function sectionOf(el) {
    return el ? el.closest('[data-cgn-section]') : null;
  }

  /* Single place that writes accordion state, so the toggle handler and the
     AJAX re-render can never disagree about it. */
  function setGroup(toggle, open) {
    if (!toggle) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    if (open) panel.removeAttribute('hidden');
    else panel.setAttribute('hidden', '');
  }

  function formOf(section) {
    return section.querySelector('[data-cgn-filter-form]');
  }

  /* Build the next URL from the filter form. The sort <select> lives in the
     toolbar but carries form="<form id>", so FormData picks it up too. */
  function buildUrl(section) {
    var form = formOf(section);
    var params = form
      ? new URLSearchParams(new FormData(form))
      : new URLSearchParams(window.location.search);

    /* Drop empty price bounds so they don't become filter.v.price.gte= */
    Array.from(params.keys()).forEach(function (key) {
      if (params.get(key) === '') params.delete(key);
    });

    /* Any filter/sort change resets to page 1. */
    params.delete('page');

    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function fetchSection(section, url) {
    var sectionId = section.getAttribute('data-cgn-section-id');
    var grid = section.querySelector('[data-cgn-grid]');

    var prev = controllers.get(section);
    if (prev) prev.abort();
    var controller = new AbortController();
    controllers.set(section, controller);

    if (grid) grid.classList.add('is-loading');

    var fetchUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + sectionId;

    fetch(fetchUrl, { signal: controller.signal })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        swapInner(section, doc, '[data-cgn-grid]');
        swapInner(section, doc, '[data-cgn-toolbar]');
        swapOuter(section, doc, '[data-cgn-pagination]');
        swapFilters(section, doc);

        var live = section.querySelector('[data-cgn-grid]');
        if (live) live.classList.remove('is-loading');
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        window.location.href = url;
      });
  }

  /* Re-render the facet rows so counts, checked state and 0-count disabling
     stay in sync with the server, WITHOUT losing which accordion groups the
     shopper has open.

     This is the same shape as Dawn's FacetFiltersForm.renderFilters: it never
     replaces the per-filter wrapper, only that wrapper's contents. Dawn relies
     on that to preserve <details open>; here the state lives on the group's
     toggle (aria-expanded) and panel (hidden), both of which sit outside the
     contents being replaced, so open groups stay open across an update.

     Groups are matched on data-cgn-group (the filter's param_name) rather than
     on label text, so a renamed facet can't silently reset every group. */
  function swapFilters(section, doc) {
    var liveForm = section.querySelector('[data-cgn-filter-form]');
    var freshForm = doc.querySelector('[data-cgn-filter-form]');
    if (!liveForm || !freshForm) return;

    /* CLEAR ALL etc. — no state to keep. */
    var liveHead = liveForm.querySelector('[data-cgn-filter-head]');
    var freshHead = freshForm.querySelector('[data-cgn-filter-head]');
    if (liveHead && freshHead) liveHead.innerHTML = freshHead.innerHTML;

    var freshGroups = Array.prototype.slice.call(freshForm.querySelectorAll('[data-cgn-group]'));
    var freshKeys = {};
    freshGroups.forEach(function (g) { freshKeys[g.getAttribute('data-cgn-group')] = true; });

    /* Drop groups the server no longer returns. */
    Array.prototype.forEach.call(liveForm.querySelectorAll('[data-cgn-group]'), function (g) {
      if (!freshKeys[g.getAttribute('data-cgn-group')]) g.remove();
    });

    var findLive = function (key) {
      return liveForm.querySelector('[data-cgn-group="' + cssEscape(key) + '"]');
    };

    freshGroups.forEach(function (fresh, i) {
      var key = fresh.getAttribute('data-cgn-group');
      var live = findLive(key);

      if (!live) {
        /* A group that appeared (e.g. a filter was cleared). Insert it in the
           server's order; it arrives closed unless the server marked it open. */
        var prev = i > 0 ? findLive(freshGroups[i - 1].getAttribute('data-cgn-group')) : null;
        if (prev) prev.after(fresh.cloneNode(true));
        else liveForm.appendChild(fresh.cloneNode(true));
        return;
      }

      var livePanel = live.querySelector('[data-cgn-group-panel]');
      var freshPanel = fresh.querySelector('[data-cgn-group-panel]');
      if (!livePanel || !freshPanel) return;

      /* Contents only — the wrapper, its toggle's aria-expanded and the
         panel's hidden attribute are all left exactly as the shopper left
         them. */
      livePanel.innerHTML = freshPanel.innerHTML;

      /* The value count can cross the 10-value threshold as filters narrow,
         adding/removing the section's own --scroll modifier. Adopt the server's
         whole class list rather than naming one section's modifier: the open
         state lives on the panel's `hidden` attribute, never on its classes. */
      livePanel.className = freshPanel.className;

      /* If the server now reports this group as active, force it open so an
         applied filter is never left hidden. Never force it CLOSED. */
      var freshToggle = fresh.querySelector('[data-cgn-group-toggle]');
      if (freshToggle && freshToggle.getAttribute('aria-expanded') === 'true') {
        setGroup(live.querySelector('[data-cgn-group-toggle]'), true);
      }
    });
  }

  function cssEscape(v) {
    return window.CSS && CSS.escape ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&');
  }

  function swapInner(section, doc, selector) {
    var live = section.querySelector(selector);
    var fresh = doc.querySelector(selector);
    if (live && fresh) live.innerHTML = fresh.innerHTML;
  }

  function swapOuter(section, doc, selector) {
    var live = section.querySelector(selector);
    var fresh = doc.querySelector(selector);
    if (live && fresh) {
      live.outerHTML = fresh.outerHTML;
    } else if (live) {
      live.remove();
    } else if (fresh) {
      /* Pagination appeared where there was none (e.g. filter cleared). */
      var results = section.querySelector('[data-cgn-results]');
      if (results) results.appendChild(fresh.cloneNode(true));
    }
  }

  function pushAndFetch(section, url) {
    window.history.pushState({}, '', url);
    fetchSection(section, url);
  }

  /* ── Filter checkbox change (debounced) + sort change (immediate) ───────── */
  document.addEventListener('change', function (e) {
    var section = sectionOf(e.target);
    if (!section) return;

    if (e.target.matches('[data-cgn-sort]')) {
      pushAndFetch(section, buildUrl(section));
      return;
    }

    if (!e.target.matches('[data-cgn-filter-input]')) return;

    clearTimeout(timers.get(section));
    timers.set(
      section,
      setTimeout(function () {
        pushAndFetch(section, buildUrl(section));
      }, DEBOUNCE_MS)
    );
  });

  /* ── Price range: only applies on the button, like the old engine ───────── */
  /* ── Chips, CLEAR ALL, pagination: follow the href Shopify already built ── */
  document.addEventListener('click', function (e) {
    var section = sectionOf(e.target);
    if (!section) return;

    /* Accordion toggle. Purely presentational — it returns before any of the
       URL-building branches below, so opening or closing a group never
       triggers a filter request. The buttons are type="button", so they also
       can't submit the form natively. */
    var groupToggle = e.target.closest('[data-cgn-group-toggle]');
    if (groupToggle) {
      setGroup(groupToggle, groupToggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    if (e.target.closest('[data-cgn-price-apply]')) {
      pushAndFetch(section, buildUrl(section));
      return;
    }

    var drawerOpen = e.target.closest('[data-cgn-drawer-open]');
    if (drawerOpen) {
      setDrawer(section, true);
      return;
    }

    if (e.target.closest('[data-cgn-drawer-close]')) {
      setDrawer(section, false);
      return;
    }

    var link = e.target.closest('[data-cgn-filter-remove], [data-cgn-clear-all]');
    if (link) {
      e.preventDefault();
      pushAndFetch(section, link.getAttribute('href'));
      return;
    }

    var page = e.target.closest('[data-cgn-page-link]');
    if (page) {
      e.preventDefault();
      var url = page.getAttribute('href');
      window.history.pushState({}, '', url);
      fetchSection(section, url);
      var top = section.querySelector('[data-cgn-toolbar]');
      if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  /* Enter inside a price field should apply, not submit the form natively. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    if (!e.target.matches || !e.target.matches('[data-cgn-price-input]')) return;
    var section = sectionOf(e.target);
    if (!section) return;
    e.preventDefault();
    pushAndFetch(section, buildUrl(section));
  });

  /* ── Mobile drawer — same open/close contract as header-new ─────────────── */
  function setDrawer(section, open) {
    var panel = section.querySelector('[data-cgn-panel]');
    var backdrop = section.parentElement
      ? section.parentElement.querySelector('[data-cgn-backdrop]')
      : null;
    var trigger = section.querySelector('[data-cgn-drawer-open]');
    if (!panel) return;

    panel.classList.toggle('is-open', open);
    if (backdrop) {
      if (open) backdrop.removeAttribute('hidden');
      backdrop.classList.toggle('is-open', open);
      if (!open) {
        setTimeout(function () {
          if (!panel.classList.contains('is-open')) backdrop.setAttribute('hidden', '');
        }, 300);
      }
    }
    document.body.classList.toggle('col-filters-open', open);
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open) {
      var close = panel.querySelector('[data-cgn-drawer-close]');
      if (close) close.focus();
    } else if (trigger) {
      trigger.focus();
    }
  }

  document.addEventListener('click', function (e) {
    var backdrop = e.target.closest('[data-cgn-backdrop]');
    if (!backdrop) return;
    var section = backdrop.parentElement
      ? backdrop.parentElement.querySelector('[data-cgn-section]')
      : null;
    if (section) setDrawer(section, false);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-cgn-panel].is-open').forEach(function (panel) {
      var section = sectionOf(panel);
      if (section) setDrawer(section, false);
    });
  });

  /* Focus trap while the drawer is open. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var panel = document.querySelector('[data-cgn-panel].is-open');
    if (!panel) return;
    var focusable = Array.from(
      panel.querySelectorAll('button, [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ── Back / forward ─────────────────────────────────────────────────────── */
  window.addEventListener('popstate', function () {
    document.querySelectorAll('[data-cgn-section]').forEach(function (section) {
      fetchSection(section, window.location.href);
    });
  });
})();
